import * as React from "react";
import { useContext, useState } from "react";
import { Typography } from "@progress/kendo-react-common/dist/es/typography/Typography";
import { Button } from "@progress/kendo-react-buttons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { GridDataStateChangeEvent } from "@progress/kendo-react-grid";
import { InputChangeEvent } from "@progress/kendo-react-inputs/dist/npm/input/interfaces/InputChangeEvent";
import { DataResult, State } from "@progress/kendo-data-query";
import { useTheme } from "@mui/material";
import { ToggleButton } from "../PlantDock/ToggleButton";
import { StyledFlexAlignEnd } from "../FtlRouteHeader/FtlRouteHeader";
import TextInput from "../../../../../Layout/Inputs/TextInput";
import { isProjectReadOnly as isProjectReadonly } from "../../../IsProjectReadOnly";
import StyledKendoGrid from "../../../../../Grid/StyledKendoGrid";
import { EditProjectContext } from "../../../EditProjectContext";
import CommandColumn from "../../../../../Grid/ControlColumn/CommandColumn";
import { CommandDeleteRowButton } from "../../../../../Grid/ControlColumn/CommandDeleteRowButton";
import { OdcNetworkGridColumns } from "./OdcNetworkGridColumns";
import { OdcNetwork } from "../../../../ViewModel/OdcNetwork";
import { addOdcInputOnClickHandler } from "./AddOdcInputOnClickHandler";
import { deleteOdcOnClick } from "./DeleteOdcOnClick";

type Props = {
  odcs: Array<OdcNetwork>;
  ftlRouteId: number;
};

export default function ODCNetworkForLTLReturnRouting({ odcs, ftlRouteId }: Props) {
  const { project } = useContext(EditProjectContext);
  const isReadonly = isProjectReadonly(project);
  const editProjectContext = useContext(EditProjectContext);
  const idField = "Id";

  const [textInput, setTextInput] = React.useState("");

  const [expanded, setExpanded] = useState<Array<number>>([]);

  const toggleCardExpansion = (panel: number) => {
    if (expanded.includes(panel)) {
      const array = [...expanded]; // make a separate copy of the array
      const index = array.indexOf(panel);
      if (index !== -1) {
        array.splice(index, 1);
        setExpanded(array);
      }
    } else {
      setExpanded([...expanded, panel]);
    }
  };

  const [dataState, setDataState] = React.useState<State>({});
  const dataResult: DataResult = {
    data: odcs,
    total: odcs.length,
  };
  const [editState, setEditState] = React.useState<number>();
  const commandColumn = CommandColumn({
    // eslint-disable-next-line react/no-unstable-nested-components
    commands: (dataItem: OdcNetwork) => [
      <CommandDeleteRowButton
        disabled={isReadonly}
        key="CommandRemoveButton"
        onClick={async () => {
          await deleteOdcOnClick(dataItem, ftlRouteId, project.ProjectId, editProjectContext);
        }}
      />,
    ],
  });

  const handleOdcInput = (event: InputChangeEvent) => {
    const { value } = event.target;
    const inputString = value as string;
    setTextInput(inputString);
  };

  const InputRow = isReadonly ? (
    <div />
  ) : (
    <StyledFlexAlignEnd style={{ marginBottom: "1.5rem" }}>
      <TextInput
        label="ODC.Networktype"
        onChange={handleOdcInput}
        name="odc"
        value={textInput}
        disabled={isReadonly}
        style={{ width: "202px" }}
      />
      {isReadonly ? (
        <Button disabled>
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      ) : (
        <Button
          onClick={() => addOdcInputOnClickHandler(textInput, ftlRouteId, editProjectContext)}
        >
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      )}
    </StyledFlexAlignEnd>
  );

  const theme = useTheme();
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      {/* eslint-disable-next-line react/jsx-pascal-case */}
      <Typography.h4>
        ODC/Network For LTL Return Routing
        <div
          style={{
            display: "inline-block",
            float: "right",
          }}
        />
        <ToggleButton
          onClick={() => toggleCardExpansion(ftlRouteId)}
          expanded={expanded.includes(ftlRouteId)}
        />
      </Typography.h4>

      {expanded.includes(ftlRouteId) && (
        <>
          {InputRow}
          <StyledKendoGrid
            muiTheme={theme}
            data={{
              data: dataResult.data
                .filterBy(dataState)
                .orderBy(dataState)
                .withEditfield(idField, editState),
              total: dataResult.total,
            }}
            dataItemKey={idField}
            sortable
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
            style={{ width: "380px" }}
          >
            {[...OdcNetworkGridColumns(), commandColumn]}
          </StyledKendoGrid>
        </>
      )}
    </div>
  );
}
